import React from 'react';
import { Button, CircularProgress } from '@mui/material';
import { rgbaToHex } from '../../utils/functools';
import { useAppTheme } from '../../theme/ThemeProvider/AppTheme';

const RoundedButton = ({
  children,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  loading = false,
  disabled = false,
  startIcon,
  endIcon,
  onClick,
  type = 'button',
  fullWidth = false,
  textColor,
  bgColor,
  loadingIconSize = 18,
  sx,
  ...props
}) => {
  const { colors } = useAppTheme();

  const colorMap = {
    primary: {
      color: colors['bg'],
      bg: colors['primary'],
    },
    error: {
      color: '#FFFFFF',
      bg: colors['error'],
    },
    warning: {
      color: '#FFFFFF',
      bg: colors['warning'],
    },
    success: {
      color: '#FFFFFF',
      bg: colors['success'],
    },
    info: {
      color: '#FFFFFF',
      bg: colors['info'],
    },
    default: {
      color: colors['text'],
      bg: colors['bg'],
    },
  };

  if (!textColor) {
    if (Object.hasOwn(colorMap, color)) {
      textColor = colorMap[color]['color'];
    } else {
      textColor = colorMap['default']['color'];
    }
  } else if (textColor?.startsWith('rgb')) {
    textColor = textColor.replace('rgb(', '').replace(')', '');
    textColor = rgbaToHex(...textColor.split(',').map(Number));
  }

  if (!bgColor) {
    if (Object.hasOwn(colorMap, color)) {
      bgColor = colorMap[color]['bg'];
    } else {
      bgColor = colorMap['default']['bg'];
    }
  } else if (bgColor?.startsWith('rgb')) {
    bgColor = bgColor.replace('rgb(', '').replace(')', '');
    bgColor = rgbaToHex(...bgColor.split(',').map(Number));
  }

  if (variant === 'outlined') {
    textColor = bgColor;
  }

  return (
    <Button
      variant={variant}
      size={size}
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
      fullWidth={fullWidth}
      startIcon={loading ? <CircularProgress size={18} color="inherit" /> : startIcon}
      endIcon={!loading ? endIcon : null}
      sx={{
        borderRadius: '12px',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: size === 'large' ? '16px' : '14px',
        py: size === 'large' ? 1.75 : 1.5,
        px: size === 'large' ? 4 : 3,
        transition: 'all 0.2s ease',

        bgcolor: `color-mix(in srgb, ${bgColor} ${variant === 'outlined' ? 5 : 90}%, transparent)`,
        color: textColor,
        boxShadow: `0 4px 12px color-mix(in srgb, ${bgColor} 10%, transparent)`,
        border: variant === 'outlined' ? `1px solid ${textColor}` : 'none',
        '&:hover': {
          bgcolor: `color-mix(in srgb, ${bgColor} ${variant === 'outlined' ? 10 : 100}%, transparent)`,
          border: variant === 'outlined' ? `1px solid ${textColor}` : 'none',
          boxShadow: `0 6px 16px color-mix(in srgb, ${bgColor} 20%, transparent)`,
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        '&.Mui-disabled': {
          bgcolor: 'var(--color-action-disabled)',
          color: 'var(--color-text-muted)',
          boxShadow: 'none',
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

export default RoundedButton;
